import React, { useEffect, useState } from "react";
import Breadcrumb from "../components/Breadcrumb";
import {
  LuLogOut,
  LuMapPin,
  LuShoppingBag,
  LuUser,
  LuMail,
  LuPhone,
  LuCalendar,
} from "react-icons/lu";
import { BsGenderMale } from "react-icons/bs";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getUserdataById,
  UpdateUserProfile,
  VerifyOTPUser,
  SendOTPUser,
} from "../API/LoginAPI";
import { FaUser } from "react-icons/fa";
import Config from "../API/Config";
import FormLoader from "../components/FormLoader";
import { toast } from "sonner";
import useLogout from "../utils/logout";

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const logout = useLogout();
  const [FormLoading, setFormLoading] = useState(false);

  const [userData, setUserData] = useState(null);

  // OTP related states
  const [otpPopup, setOtpPopup] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [newMobile, setNewMobile] = useState("");
  const [newCCode, setNewCCode] = useState("+91");
  const [showMobilePopup, setShowMobilePopup] = useState(false);

  const countries = [
    { code: "+91", name: "India", flag: "https://flagcdn.com/w20/in.png" },
    { code: "+1", name: "USA", flag: "https://flagcdn.com/w20/us.png" },
  ];

  // Fetch user data
  const fetchData = async () => {
    try {
      const res = await getUserdataById();
      if (res) {
        setUserData(res);
        formik.setValues({
          Name: res.Name || "",
          Email: res.Email || "",
          CCODE: res.CCODE || "+91",
          Phone: res.Phone || "",
          DOB: res.DOB ? res.DOB.split("T")[0] : "",
          Gender: res.Gender || "",
          AnniversaryDate: res.AnniversaryDate
            ? res.AnniversaryDate.split("T")[0]
            : "",
        });
        setNewCCode(res.CCODE || "+91");
      }
    } catch (err) {
      console.error("Error fetching user:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      Name: "",
      Email: "",
      CCODE: "+91",
      Phone: "",
      DOB: "",
      Gender: "",
      AnniversaryDate: "",
    },
    validationSchema: Yup.object({
      Name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      // Check if mobile changed
      if (
        values.Phone !== userData?.Phone ||
        values.CCODE !== userData?.CCODE
      ) {
        setNewMobile(values.Phone);
        setNewCCode(values.CCODE);
        setShowMobilePopup(true);
        return;
      }
      // Update normally if phone unchanged
      await updateProfile(values);
    },
  });

  const updateProfile = async (values) => {
    setFormLoading(true);
    try {
      const result = await UpdateUserProfile(values);
      if (result.status === true) {
        fetchData();
      }
    } catch (err) {
      console.error("Error updating profile:", err);
    } finally {
      setFormLoading(false);
    }
  };

  // OTP Handlers
  const handleOtpChange = (val, idx) => {
    if (/^[0-9]?$/.test(val)) {
      const newOtpArr = [...otp];
      newOtpArr[idx] = val;
      setOtp(newOtpArr);
      if (val && idx < 3) document.getElementById(`otp-${idx + 1}`).focus();
    }
  };

  const handleSendOtp = async () => {
    try {
      const payload = { CCODE: newCCode, EOP: newMobile };
      const data = await SendOTPUser(payload);

      if (data.status === true) {
        toast.success(data.message);
        setOtp(["", "", "", ""]);
        setResendTimer(60);
        setOtpPopup(true);
      } else {
        setOtpPopup(false);
        setShowMobilePopup(false);
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 4) {
      toast.error("Enter valid 4 digit OTP");
      return;
    }
    setFormLoading(true);
    try {
      const payload = { CCODE: newCCode, EOP: newMobile, OTP: enteredOtp };
      const data = await VerifyOTPUser(payload);
      if (data.status === true) {
        await updateProfile({ ...formik.values, Phone: newMobile, CCODE: newCCode });
        setOtpPopup(false);
        setShowMobilePopup(false);
      } else {
        toast.error("Invalid OTP");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const payload = { CCODE: newCCode, EOP: newMobile };
      const data = await SendOTPUser(payload);
      if (data.status === true) {
        setOtp(["", "", "", ""]);
        toast.success("OTP resent");
        setResendTimer(60);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const closeMobilePopup = () => {
    setShowMobilePopup(false);
    setOtpPopup(false);
    setOtp(["", "", "", ""]);
  };

  const UserName = Config.getUserName();
  const UserEmail = Config.getUserEmail();

  return (
    <>
      <Breadcrumb title="Profile" path="Profile" />
      {FormLoading && <FormLoader loading={FormLoading} />}
      <div className="container mx-auto px-4 md:py-20 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Section */}
          <div className="bg-[#f9f9f9] rounded-lg overflow-hidden">
            <div className="relative md:h-36 h-24 bg-theme2">
              <div className="bg-pink-700 uppercase absolute left-1/2 md:-bottom-11 -bottom-10 transform -translate-x-1/2 text-white md:w-24 w-20 md:h-24 h-20 border-4 border-white flex items-center justify-center rounded-full text-5xl shadow-md">
                {UserName ? UserName.charAt(0) : <FaUser />}
              </div>
            </div>
            <div className="flex flex-col items-center mt-12 px-4">
              <h3 className="font-semibold text-lg">{UserName}</h3>
              <p className="text-sm text-gray-500">{UserEmail}</p>
            </div>
            <ul className="md:mt-8 mt-4 md:pb-6 pb-2 flex flex-row md:flex-col md:space-y-2 space-x-2 md:space-x-0 md:pl-4 overflow-x-auto md:overflow-visible flex-nowrap">
              <Link to={"/profile"} className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-theme2 font-semibold cursor-pointer md:border-b-0 md:border-r-4 border-theme2">
                <LuUser className="md:text-lg text-sm" /> Edit Profile
              </Link>
              <Link to={"/my-order"} className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition">
                <LuShoppingBag className="md:text-lg text-sm" /> My Order
              </Link>
              <Link to={"/my-address"} className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition">
                <LuMapPin className="md:text-lg text-sm" /> My Address
              </Link>
              <Link onClick={logout} className="flex items-center whitespace-nowrap md:text-lg text-sm py-1.5 px-2 md:gap-3 gap-1 text-gray-600 cursor-pointer hover:text-theme2 transition">
                <LuLogOut className="md:text-lg text-sm" /> Log Out
              </Link>
            </ul>
          </div>

          {/* Right Section */}
          <div className="lg:col-span-3 bg-[#f9f9f9] rounded-lg shadow md:p-6 p-4">
            <h2 className="md:text-2xl text-darktext text-xl font-semibold pb-3 mb-4 mt-2">
              Edit Profile
            </h2>
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
                {/* Name */}
                <div className="bg-white md:p-4 p-3 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <LuUser className="text-theme2" /> Name :
                  </p>
                  <input
                    type="text"
                    name="Name"
                    placeholder="Enter Your Name"
                    value={formik.values.Name}
                    onChange={formik.handleChange}
                    className="w-full border px-3 py-2 rounded"
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <p className="text-red-500 text-sm">{formik.errors.Name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="bg-white md:p-4 p-2 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <LuMail className="text-theme2" /> Email :
                  </p>
                  <input
                    type="email"
                    name="Email"
                    placeholder="Enter Your Email"
                    value={formik.values.Email}
                    onChange={formik.handleChange}
                    className="w-full border px-3 py-2 rounded"
                  />
                  {formik.touched.Email && formik.errors.Email && (
                    <p className="text-red-500 text-sm">{formik.errors.Email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="bg-white md:p-4 p-2 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <LuPhone className="text-theme2" /> Phone Number :
                  </p>
                  <div className="flex items-center w-full border px-3 py-2 rounded">
                    <select
                      name="CCODE"
                      value={formik.values.CCODE}
                      onChange={formik.handleChange}
                      className="border-r pr-2"
                    >
                      {countries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      name="Phone"
                      placeholder="Enter your number"
                      value={formik.values.Phone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full px-2 py-1 outline-none"
                    />
                  </div>
                  {formik.touched.Phone && formik.errors.Phone && (
                    <p className="text-red-500 text-sm">{formik.errors.Phone}</p>
                  )}
                </div>

                {/* DOB */}
                <div className="bg-white md:p-4 p-2 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <LuCalendar className="text-theme2" /> Date Of Birth :
                  </p>
                  <input
                    type="date"
                    name="DOB"
                    value={formik.values.DOB}
                    onChange={formik.handleChange}
                    className="w-full border px-3 py-2 rounded"
                  />
                  {formik.touched.DOB && formik.errors.DOB && (
                    <p className="text-red-500 text-sm">{formik.errors.DOB}</p>
                  )}
                </div>

                {/* Anniversary */}
                <div className="bg-white md:p-4 p-2 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <LuCalendar className="text-theme2" /> Anniversary Date :
                  </p>
                  <input
                    type="date"
                    name="AnniversaryDate"
                    value={formik.values.AnniversaryDate}
                    onChange={formik.handleChange}
                    className="w-full border px-3 py-2 rounded"
                  />
                  {formik.touched.AnniversaryDate && formik.errors.AnniversaryDate && (
                    <p className="text-red-500 text-sm">{formik.errors.AnniversaryDate}</p>
                  )}
                </div>

                {/* Gender */}
                <div className="bg-white md:p-4 p-2 rounded text-darktext">
                  <p className="flex items-center gap-2 font-medium md:text-lg text-sm mb-2">
                    <BsGenderMale className="text-theme2" /> Gender :
                  </p>
                  <div className="flex gap-6">
                    {["male", "female", "other"].map((g) => (
                      <label key={g} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="Gender"
                          value={g}
                          checked={formik.values.Gender === g}
                          onChange={formik.handleChange}
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                  {formik.touched.Gender && formik.errors.Gender && (
                    <p className="text-red-500 text-sm">{formik.errors.Gender}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-theme2 text-white px-6 text-sm md:text-base py-2 rounded-full hover:bg-black transition"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Mobile Change Popup */}
      {showMobilePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8 text-center relative">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-black"
              onClick={closeMobilePopup}
            >
              ✕
            </button>
            <h2 className="md:text-2xl text-xl font-semibold mb-2">
              Verify Number
            </h2>
            <p className="text-contentcolor mb-4">

              Youhave changed your phone number to <b>{newMobile}</b>. Please click on Send OTP to verify your new Phone Number
            </p>
            <button
              onClick={handleSendOtp}
              className="w-full bg-theme2 hover:bg-black text-white py-3 rounded-md font-semibold mb-2"
            >
              Send OTP
            </button>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {otpPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8 text-center relative">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-black"
              onClick={closeMobilePopup}
            >
              ✕
            </button>
            <h2 className="md:text-2xl text-xl font-semibold mb-2">
              Verify Your OTP
            </h2>
            <p className="text-contentcolor mb-6">
              Enter the 4-digit OTP sent to your new number.
            </p>
            <div className="flex justify-center gap-3 mb-6">
              {otp.map((val, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="text"
                  maxLength="1"
                  value={val}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  className="w-12 h-12 border border-gray-600 rounded-md text-center text-lg focus:outline-none focus:border-theme2"
                />
              ))}
            </div>
            <button
              onClick={handleVerifyOtp}
              className="w-full bg-theme2 hover:bg-black text-white py-4 rounded-md font-semibold mb-4"
            >
              Verify OTP
            </button>
            <p className="text-contentcolor">
              Didn’t receive the code?{" "}
              <button
                onClick={handleResendOtp}
                className={`font-semibold ${resendTimer > 0 ? "text-contentcolor cursor-not-allowed" : "text-theme2"
                  }`}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
